import Phaser from "phaser";
import {
  BRANCH_LIFT,
  BRANCH_OVERHEAD_DISTANCE,
  FINISH_SEQUENCE_MS,
  GAME_OVER_HIT_MS,
  GAME_OVER_SEQUENCE_MS,
  GAME_HEIGHT,
  GAME_WIDTH,
  JUMP_BUFFER_MS,
  MAX_FRAME_DELTA_MS,
  bandForElapsed,
  smoothedApproachLeadMs,
} from "../constants";
import { Bear } from "../entities/Bear";
import { RunState } from "../logic/runState";
import { type FoodId } from "../data/foods";
import { isFoodKind } from "../data/encounterPatterns";
import { isHazardClearKind } from "../logic/scoring";
import { InputController } from "../systems/InputController";
import {
  EncounterDirector,
  destinationLane,
  displayLane,
  laneFitScale,
  WOLF_CROSS_FAR_DEPTH,
  WOLF_CROSS_NEAR_DEPTH,
  type ScheduledEncounter,
} from "../systems/EncounterDirector";
import { avoidedHazard, detectCollisions, isInHitWindow, sameLane } from "../systems/CollisionSystem";
import type { BearCollisionPose } from "../systems/CollisionSystem";
import {
  approachAlpha,
  distanceFromTiming,
  ProjectionContext,
  shouldRecycle,
} from "../systems/RunnerProjection";
import { ForestBackdrop } from "../systems/ForestBackdrop";
import { motionFx } from "../systems/motionFx";
import { audioManager } from "../systems/AudioManager";
import { Hud } from "../ui/Hud";
import { COLORS } from "../ui/theme";
import { applySpriteOrigin } from "../assets/loader";
import { setDepthIfChanged } from "../setDepthIfChanged";
import { ensurePrimitiveTextures, sizeBlob } from "../assets/primitives";
import {
  createFxSpritePool,
  createFxTextPool,
  resetFxSprite,
  type FxSpritePool,
  type FxTextPool,
} from "../systems/fxPool";
import { WorldSpritePool, type WorldSprite } from "../systems/WorldSpritePool";

const FOOD_FLOAT_COLOR: Record<FoodId, string> = {
  berries: COLORS.berry,
  apple: COLORS.berry,
  acorns: "#c4a574",
  honey: COLORS.honey,
  fish: "#d5e4ee",
};

export class GameScene extends Phaser.Scene {
  private run!: RunState;
  private bear!: Bear;
  private inputCtl!: InputController;
  private director!: EncounterDirector;
  private forest!: ForestBackdrop;
  private projection!: ProjectionContext;
  private hud!: Hud;
  private world: WorldSprite[] = [];
  private worldPool!: WorldSpritePool;
  private fxSprites!: FxSpritePool;
  private fxTexts!: FxTextPool;
  private finishing = false;
  private vignette!: Phaser.GameObjects.Rectangle;
  private touch = { left: false, right: false, up: false, down: false };
  private swipeStart: { x: number; y: number } | null = null;
  private keys!: {
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
    a: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
    w: Phaser.Input.Keyboard.Key;
    s: Phaser.Input.Keyboard.Key;
    space: Phaser.Input.Keyboard.Key;
  };

  constructor() {
    super("Game");
  }

  create(): void {
    this.finishing = false;
    this.world = [];
    this.touch = { left: false, right: false, up: false, down: false };
    this.swipeStart = null;
    this.run = new RunState();
    this.director = new EncounterDirector();
    this.inputCtl = new InputController(JUMP_BUFFER_MS);
    this.projection = new ProjectionContext({ width: this.scale.width, height: this.scale.height });
    ensurePrimitiveTextures(this);
    this.worldPool = new WorldSpritePool(this);
    this.fxSprites = createFxSpritePool(this);
    this.fxTexts = createFxTextPool(this);
    this.forest = new ForestBackdrop(this, this.projection, this.fxSprites);
    this.bear = new Bear(this, this.projection, this.fxSprites);
    this.hud = new Hud(this, () => this.pauseRun());
    this.vignette = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x7a1c12, 0);
    this.vignette.setDepth(1900).setScrollFactor(0);

    const keyboard = this.input.keyboard!;
    keyboard.enabled = true;
    this.keys = keyboard.addKeys({
      left: Phaser.Input.Keyboard.KeyCodes.LEFT,
      right: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      up: Phaser.Input.Keyboard.KeyCodes.UP,
      down: Phaser.Input.Keyboard.KeyCodes.DOWN,
      a: Phaser.Input.Keyboard.KeyCodes.A,
      d: Phaser.Input.Keyboard.KeyCodes.D,
      w: Phaser.Input.Keyboard.KeyCodes.W,
      s: Phaser.Input.Keyboard.KeyCodes.S,
      space: Phaser.Input.Keyboard.KeyCodes.SPACE,
    }) as GameScene["keys"];
    keyboard.on("keydown-ESC", this.onEscape, this);
    this.game.canvas.setAttribute("tabindex", "0");
    this.game.canvas.focus();

    this.bindTouchControls();
    audioManager.playMusic("run");
    this.game.events.on(Phaser.Core.Events.BLUR, this.onBlur, this);
    this.game.events.on(Phaser.Core.Events.HIDDEN, this.onBlur, this);
    this.events.on(Phaser.Scenes.Events.RESUME, this.onRunResume, this);
    this.scale.on(Phaser.Scale.Events.RESIZE, this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off(Phaser.Core.Events.BLUR, this.onBlur, this);
      this.game.events.off(Phaser.Core.Events.HIDDEN, this.onBlur, this);
      this.events.off(Phaser.Scenes.Events.RESUME, this.onRunResume, this);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.onResize, this);
      keyboard.off("keydown-ESC", this.onEscape, this);
      this.input.off("pointerdown", this.onWorldPointerDown, this);
      this.input.off("pointerup", this.onWorldPointerUp, this);
      this.touch = { left: false, right: false, up: false, down: false };
      this.swipeStart = null;
      audioManager.resumeOutput();
      this.world = [];
      this.worldPool.destroy();
      this.fxSprites.destroy();
      this.fxTexts.destroy();
    });
  }

  update(_time: number, delta: number): void {
    if (this.scene.isPaused()) {
      return;
    }
    const dt = Math.min(delta, MAX_FRAME_DELTA_MS);
    this.inputCtl.setHeld({
      left: this.keys.left.isDown || this.keys.a.isDown || this.touch.left,
      right: this.keys.right.isDown || this.keys.d.isDown || this.touch.right,
      up: this.keys.up.isDown || this.keys.w.isDown || this.keys.space.isDown || this.touch.up,
      down: this.keys.down.isDown || this.keys.s.isDown || this.touch.down,
      escape: false,
      confirm: false,
    });

    if (this.run.phase === "gameOver") {
      this.bear.update(dt, false);
      this.syncMotion();
      this.bear.applyPose();
      this.forest.update(this.run.elapsedMs, this.run.progress, 0, 0, 0);
      this.advanceWorld();
      return;
    }

    if (this.finishing) {
      this.bear.update(dt, false);
      this.syncMotion();
      this.bear.applyPose();
      this.forest.update(this.run.elapsedMs, 1, dt, this.worldDelta(dt), 1);
      this.advanceWorld();
      return;
    }

    const intent = this.inputCtl.poll(this.run.elapsedMs);
    if (intent.laneDelta !== 0) {
      const next = this.inputCtl.clampLane(this.bear.lane, intent.laneDelta);
      this.bear.tryLaneChange(next);
    }
    if (intent.jump && this.bear.tryJump()) {
      this.inputCtl.consumeJump();
    }
    if (intent.duck && this.bear.tryDuck()) {
      this.inputCtl.consumeDuck();
    }

    const { justFinished } = this.run.tick(dt);
    this.bear.update(dt, this.run.isInvulnerable());
    this.syncMotion();
    this.bear.applyPose();
    const band = bandForElapsed(this.run.elapsedMs);
    const speedT = band === "tutorial" ? 0.28 : band === "normal" ? 0.52 : band === "intense" ? 0.82 : 1;
    this.forest.update(this.run.elapsedMs, this.run.progress, dt, this.worldDelta(dt), speedT);
    this.spawnNew();
    this.advanceWorld();
    const died = this.resolveCollisions();
    this.hud.refresh(this.run.snapshot());

    if (justFinished) {
      this.beginFinish();
    }
    if (died) {
      this.skippableDelay(GAME_OVER_HIT_MS + GAME_OVER_SEQUENCE_MS, () => {
        this.scene.start("GameOver", { score: this.run.score });
      });
    }
  }

  private worldDelta(dt: number): number {
    return dt / smoothedApproachLeadMs(this.run.elapsedMs);
  }

  private syncMotion(): void {
    const fx = motionFx();
    this.projection.setMotion({
      leanX: this.bear.visualLane * fx.cameraLeanPx,
      bobY: this.bear.landingKick * fx.landingKickPx,
    });
  }

  private onResize = (gameSize: Phaser.Structs.Size): void => {
    this.projection.setViewport({ width: gameSize.width, height: gameSize.height });
    this.forest.layout();
  };

  private spawnNew(): void {
    const spawned = this.director.update(this.run.elapsedMs);
    for (const encounter of spawned) {
      this.world.push(this.makeWorldItem(encounter));
    }
  }

  private makeWorldItem(encounter: ScheduledEncounter): WorldSprite {
    return this.worldPool.acquire(encounter);
  }

  private recycleWorldItem(item: WorldSprite): void {
    this.worldPool.release(item);
  }

  private advanceWorld(): void {
    const { frame } = this.projection;
    const next: WorldSprite[] = [];
    for (const item of this.world) {
      const distance = distanceFromTiming(
        item.encounter.reachAtMs,
        this.run.elapsedMs,
        item.encounter.leadMs,
        frame.maxDistance,
      );
      if (shouldRecycle(distance, frame)) {
        item.encounter.consumed = true;
        this.recycleWorldItem(item);
        continue;
      }
      const lane = displayLane(item.encounter, this.run.elapsedMs);
      const pose = this.projection.project(distance, lane);
      const alpha = approachAlpha(distance, frame);
      item.sprite.setPosition(pose.x, pose.y);
      if (item.encounter.kind === "branch") {
        item.sprite.y = pose.y - BRANCH_LIFT * pose.scale;
      }
      if (item.encounter.kind === "ravine") {
        const laneWidth = pose.roadHalfWidth * frame.laneSpread;
        item.sprite.setScale(laneWidth / 118, Math.max(0.35, pose.scale * 1.05));
      } else {
        const srcW = item.sprite.width || 256;
        item.sprite.setScale(laneFitScale(item.encounter.kind, pose, srcW, frame.laneSpread));
      }
      const overhead = item.encounter.kind === "branch" && distance < BRANCH_OVERHEAD_DISTANCE;
      setDepthIfChanged(item.sprite, pose.depth + (item.encounter.kind === "ravine" ? -8 : overhead ? 50 : 0));
      item.sprite.setAlpha(alpha);
      item.sprite.setVisible(distance < frame.maxDistance * 1.02);
      item.shadow.setPosition(pose.x, pose.y + 8 * pose.scale);
      sizeBlob(
        item.shadow,
        64 * pose.scale * (item.encounter.kind === "ravine" ? 1.4 : 1),
        18 * pose.scale * 0.7,
      );
      setDepthIfChanged(item.shadow, pose.depth - 2);
      item.shadow.setAlpha(0.28 * alpha);
      item.shadow.setVisible(distance < frame.maxDistance * 1.02 && item.encounter.kind !== "branch" && item.encounter.kind !== "ravine");
      if (item.glint) {
        item.glint.setPosition(pose.x + 10 * pose.scale, pose.y - 16 * pose.scale);
        item.glint.setScale(pose.scale * 0.45);
        setDepthIfChanged(item.glint, pose.depth + 2);
        item.glint.setAlpha((0.55 + Math.sin(this.run.elapsedMs / 180) * 0.3) * alpha);
      }

      if (item.encounter.kind === "wolf" && item.sprite instanceof Phaser.GameObjects.Sprite) {
        this.updateWolfAnim(item, distance / frame.maxDistance, destinationLane(item.encounter));
        applySpriteOrigin(item.sprite);
      }
      next.push(item);
    }
    this.world = next;
  }

  private updateWolfAnim(item: WorldSprite, depth: number, lane: number): void {
    if (!(item.sprite instanceof Phaser.GameObjects.Sprite) || item.dodgePlayed) {
      return;
    }
    if (item.wolfAnim === "wolf-warning") {
      return;
    }
    const from = item.encounter.wolfFrom ?? item.encounter.lane;
    const to = item.encounter.wolfTo ?? item.encounter.lane;
    const crossing = from !== to;
    let next = "wolf-run-toward";
    if (crossing && depth < WOLF_CROSS_FAR_DEPTH && depth > WOLF_CROSS_NEAR_DEPTH) {
      next = to < from ? "wolf-cross-left" : "wolf-cross-right";
    }
    if (item.wolfAnim !== next && this.anims.exists(next)) {
      item.wolfAnim = next;
      item.sprite.play(next);
    }
    if (!item.warned && depth < 0.42 && depth > 0.18) {
      item.warned = true;
      this.flashLane(lane);
      if (this.anims.exists("wolf-warning")) {
        item.wolfAnim = "wolf-warning";
        item.sprite.play("wolf-warning");
        const epoch = item.animEpoch;
        item.sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
          if (item.animEpoch !== epoch) {
            return;
          }
          if (item.sprite.active && item.sprite instanceof Phaser.GameObjects.Sprite) {
            const resume = crossing
              ? to < from
                ? "wolf-cross-left"
                : "wolf-cross-right"
              : "wolf-run-toward";
            item.wolfAnim = resume;
            if (this.anims.exists(resume)) {
              item.sprite.play(resume);
              applySpriteOrigin(item.sprite);
            }
          }
        });
      }
    }
  }

  private flashLane(lane: number): void {
    if (!this.fxSprites.alive) {
      return;
    }
    const pose = this.projection.project(0.08, lane);
    const marker = this.fxSprites.acquire();
    resetFxSprite(marker, "icon-life-bear-paw");
    marker.setPosition(pose.x, pose.y + 18);
    marker.setDisplaySize(36, 40).setAlpha(0.9).setDepth(pose.depth + 40).setTint(0xc44536);
    this.tweens.add({
      targets: marker,
      alpha: 0,
      y: marker.y + 12,
      duration: 700,
      onComplete: () => this.fxSprites.release(marker),
    });
  }

  private resolveCollisions(): boolean {
    let died = false;
    const pose = {
      lane: this.bear.lane,
      jumping: this.bear.jumping,
      ducking: this.bear.ducking,
      airborne: this.bear.airborne,
    };
    this.resolveWolfDodges(pose);
    const events = detectCollisions(
      this.world.map((item) => item.encounter),
      this.run.elapsedMs,
      pose,
    );

    for (const event of events) {
      event.encounter.consumed = true;
      const visual = this.world.find((item) => item.encounter.id === event.encounter.id);
      if (event.type === "food" && isFoodKind(event.encounter.kind)) {
        const crouched = this.bear.motion === "ducking";
        const points = this.run.collectFood(event.encounter.kind, crouched);
        audioManager.playSfx("pickup");
        this.floatPoints(visual?.sprite.x ?? GAME_WIDTH / 2, visual?.sprite.y ?? 400, points, event.encounter.kind, crouched);
        this.playSparkle(visual?.sprite.x ?? 0, visual?.sprite.y ?? 0);
        if (visual) {
          this.recycleWorldItem(visual);
          const index = this.world.indexOf(visual);
          if (index >= 0) {
            this.world.splice(index, 1);
          }
        }
      } else if (event.type === "avoided" && isHazardClearKind(event.encounter.kind)) {
        const points = this.run.clearHazard();
        if (points > 0) {
          audioManager.playSfx("pickup");
          this.floatPoints(visual?.sprite.x ?? GAME_WIDTH / 2, visual?.sprite.y ?? 400, points);
        }
      } else if (event.type === "hazard") {
        if (event.source === "wolf") {
          audioManager.playSfx("growl");
        }
        const { applied, gameOver } = this.run.hitHazard();
        if (applied) {
          this.bear.hit();
          this.inputCtl.consumeJump();
          this.inputCtl.consumeDuck();
          audioManager.playSfx("life");
          if (motionFx().hitShake) {
            this.cameras.main.shake(120, 0.004);
            this.flashVignette();
          }
          this.playEffect(visual?.sprite.x ?? this.bear.sprite.x, this.bear.sprite.y - 40, "life-lost-pulse");
        }
        if (event.encounter.kind === "wolf" && visual?.sprite instanceof Phaser.GameObjects.Sprite) {
          visual.sprite.setTint(0xc44536);
        }
        if (gameOver) {
          died = true;
          this.director.stopSpawning();
          this.bear.beginGameOver();
          audioManager.playMusic("none");
          audioManager.playSfx("gameover");
        }
      }
    }
    return died;
  }

  private resolveWolfDodges(pose: BearCollisionPose): void {
    for (const item of this.world) {
      if (item.encounter.kind !== "wolf" || item.encounter.consumed || item.dodgePlayed) {
        continue;
      }
      if (!isInHitWindow(item.encounter, this.run.elapsedMs)) {
        continue;
      }
      if (!sameLane(destinationLane(item.encounter), pose.lane)) {
        continue;
      }
      if (!avoidedHazard("wolf", pose)) {
        continue;
      }
      item.dodgePlayed = true;
      if (item.sprite instanceof Phaser.GameObjects.Sprite && this.anims.exists("wolf-dodge-reaction")) {
        item.sprite.play("wolf-dodge-reaction");
        applySpriteOrigin(item.sprite);
      }
    }
  }

  private floatPoints(x: number, y: number, points: number, food?: FoodId, crouched = false): void {
    if (!this.fxTexts.alive) {
      return;
    }
    const color = food ? FOOD_FLOAT_COLOR[food] : COLORS.cream;
    const text = this.fxTexts.acquire();
    text.setPosition(x, y - 40);
    text.setText(`+${points}`);
    text.setColor(color);
    text.setAlpha(1);
    text.setScale(crouched ? 1.12 : 1);
    text.setOrigin(0.5).setDepth(2100);
    this.tweens.add({
      targets: text,
      y: y - 118,
      scale: crouched ? 1.32 : 1.18,
      alpha: 0,
      duration: 700,
      ease: "Back.easeOut",
      onComplete: () => this.fxTexts.release(text),
    });
  }

  private playSparkle(x: number, y: number): void {
    this.playEffect(x, y, "food-pickup-sparkle");
  }

  private playEffect(x: number, y: number, key: string): void {
    if (!this.fxSprites.alive || !this.anims.exists(key)) {
      return;
    }
    const fx = this.fxSprites.acquire();
    resetFxSprite(fx, key);
    fx.setPosition(x, y);
    fx.setDepth(2050).setScale(0.45).play(key);
    fx.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => this.fxSprites.release(fx));
  }

  private flashVignette(): void {
    this.vignette.setAlpha(0.38);
    this.tweens.add({
      targets: this.vignette,
      alpha: 0,
      duration: 280,
    });
  }

  private beginFinish(): void {
    this.finishing = true;
    this.director.stopSpawning();
    this.bear.startFinish();
    audioManager.playMusic("none");
    audioManager.playSfx("fanfare");
    const interior = addCoverish(this, "cave-interior");
    interior.setDepth(1700).setAlpha(0);
    const pile = this.add.image(GAME_WIDTH / 2, 560, "cave-food-pile");
    pile.setOrigin(0.5, 1).setScale(0.72).setDepth(1710).setAlpha(0);
    this.tweens.add({ targets: [interior, pile], alpha: 1, duration: 420 });
    this.bear.sprite.setDepth(1720);
    this.bear.sprite.play("bear-celebrate");
    this.playEffect(GAME_WIDTH / 2, 240, "cave-arrival-fanfare");
    this.skippableDelay(FINISH_SEQUENCE_MS, () => {
      this.run.markCompleted();
      this.scene.start("Results", { score: this.run.score, completed: true });
    });
  }

  private skippableDelay(ms: number, fn: () => void): void {
    let done = false;
    const go = (): void => {
      if (done || !this.scene.isActive()) {
        return;
      }
      done = true;
      fn();
    };
    this.time.delayedCall(ms, go);
    this.input.once("pointerdown", go);
    this.input.keyboard?.once("keydown", go);
  }

  private onEscape = (): void => {
    this.pauseRun();
  };

  private pauseRun(): void {
    if (this.scene.isPaused() || this.finishing || this.run.phase !== "playing") {
      return;
    }
    this.run.pause();
    if (this.input.keyboard) {
      this.input.keyboard.enabled = false;
    }
    audioManager.pauseOutput();
    this.scene.pause();
    this.scene.launch("Pause");
  }

  private onRunResume = (): void => {
    if (this.input.keyboard) {
      this.input.keyboard.enabled = true;
    }
    this.run.resume();
    audioManager.resumeOutput();
  };

  private onBlur = (): void => {
    if (this.scene.isActive() && !this.scene.isPaused() && this.run.phase === "playing") {
      this.pauseRun();
    }
  };

  private onWorldPointerDown = (pointer: Phaser.Input.Pointer): void => {
    if (pointer.y < 130 || this.finishing || this.run.phase !== "playing") {
      return;
    }
    this.swipeStart = { x: pointer.x, y: pointer.y };
  };

  private onWorldPointerUp = (pointer: Phaser.Input.Pointer): void => {
    if (!this.swipeStart) {
      return;
    }
    const start = this.swipeStart;
    this.swipeStart = null;
    if (this.finishing || this.run.phase !== "playing") {
      return;
    }
    const dx = pointer.x - start.x;
    const dy = pointer.y - start.y;
    if (Math.abs(dx) < 36 && Math.abs(dy) < 36) {
      return;
    }
    if (Math.abs(dx) > Math.abs(dy)) {
      this.inputCtl.pulseLane(dx < 0 ? -1 : 1);
    } else if (dy < 0) {
      this.inputCtl.pulseJump(this.run.elapsedMs);
    } else {
      this.inputCtl.pulseDuck(this.run.elapsedMs);
    }
  };

  private bindTouchControls(): void {
    const showPad = this.sys.game.device.input.touch || window.matchMedia("(pointer: coarse)").matches;
    this.input.on("pointerdown", this.onWorldPointerDown, this);
    this.input.on("pointerup", this.onWorldPointerUp, this);

    if (!showPad) {
      return;
    }
    const makePad = (x: number, y: number, label: string, onDown: () => void, onUp: () => void): void => {
      const bg = this.add.circle(x, y, 42, 0x2a1a10, 0.42);
      bg.setDepth(3000).setScrollFactor(0).setInteractive({ useHandCursor: true });
      const text = this.add.text(x, y, label, {
        fontFamily: "Fredoka, Nunito, sans-serif",
        fontSize: "22px",
        color: "#f3e6c8",
      });
      text.setOrigin(0.5).setDepth(3001).setScrollFactor(0);
      bg.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        onDown();
      });
      bg.on("pointerup", onUp);
      bg.on("pointerout", onUp);
    };
    makePad(90, 640, "◀", () => {
      this.touch.left = true;
    }, () => {
      this.touch.left = false;
    });
    makePad(190, 640, "▶", () => {
      this.touch.right = true;
    }, () => {
      this.touch.right = false;
    });
    makePad(1090, 640, "↑", () => {
      this.touch.up = true;
    }, () => {
      this.touch.up = false;
    });
    makePad(1190, 640, "↓", () => {
      this.touch.down = true;
    }, () => {
      this.touch.down = false;
    });
  }
}

function addCoverish(scene: Phaser.Scene, key: string): Phaser.GameObjects.Image {
  const image = scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
  const scale = Math.max(GAME_WIDTH / image.width, GAME_HEIGHT / image.height);
  image.setScale(scale);
  return image;
}
